import React, { FC, useCallback, useEffect, useState } from 'react';

import {
  CloudAccountConfigRequest,
  CloudAccountStatus,
} from '@ariksa/cloud-account/api';
import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { Select } from 'app/components/DataEntry/Select';
import { PrimaryButton } from 'components/DataEntry';
import { TimePicker } from 'components/DataEntry/Time/TimePicker';
import { HistoricalTimelineIcon } from 'components/Icons';
import { successToast } from 'components/Toast';
import { OrgConfigBox } from 'containers/Settings/Components/OrganizationConfig/OrgConfigBox';
import {
  orgSettingsIntervalOptions,
  renderOrgSettingHeader,
} from 'containers/Settings/Components/OrganizationConfig/utils';
import { selectSettings } from 'containers/Settings/selectors';
import { actions } from 'containers/Settings/slice';

import 'react-day-picker/dist/style.css';

export const ConfigureAccountSync: FC = () => {
  const dispatch = useDispatch();

  const { updateAccountDiscoveryInterval, accounts } = useSelector(
    selectSettings,
  );
  const [discoveryInterval, setDiscoveryInterval] = useState(3 * 60);
  const [discoveryTime, setDiscoveryTime] = useState('10:10');
  const [discoveryIntervalType, setDiscoveryIntervalType] = useState({
    label: 'every',
    value: 'every',
  });
  const [isChanged, setIsChanged] = useState(false);

  const getCloudAccounts = useCallback(() => {
    dispatch(
      actions.getCloudAccounts({
        q: {
          status: CloudAccountStatus.Success,
          page: 1,
          size: 100,
        },
        onSuccess: res => {
          setDiscoveryInterval(res?.items[0]?.discovery_interval ?? 3 * 60);
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getCloudAccounts();
  }, [getCloudAccounts]);

  const handleUpdate = data => {
    let payload: CloudAccountConfigRequest = {
      discovery_interval: discoveryInterval ?? 120,
      uuid: accounts.data.map(a => a.uuid),
    };

    if (discoveryIntervalType.value === 'every') {
      payload = {
        discovery_interval: discoveryInterval ?? 120,
        uuid: accounts.data.map(a => a.uuid),
      };
    } else {
      payload = {
        discovery_time: discoveryTime,
        uuid: accounts.data.map(a => a.uuid),
      };
    }
    dispatch(
      actions.updateAccountDiscoveryInterval({
        q: {
          cloudAccountConfigRequest: payload,
        },
        onSuccess: () => {
          successToast({
            title: 'Successfully updated cloud account discovery interval',
          });
          getCloudAccounts();
          setIsChanged(false);
        },
      }),
    );
  };

  return (
    <OrgConfigBox>
      <Stack spacing={6}>
        {renderOrgSettingHeader({
          icon: (
            <HistoricalTimelineIcon strokeWidth={1} stroke="currentColor" />
          ),
          header: 'Configure Account Sync',
          subHeader:
            'You can choose periodic account sync or sync at a particular time ' +
            'of the day. We recommend syncs every 24 hours.',
          rightComponent: '',
        })}
        <Stack spacing={2} pl={12} ml={1}>
          <HStack spacing={4}>
            <Text color={customTheme.colors.gray[300]}>
              Perform sync with accounts
            </Text>
            <Box w={'150px'}>
              <Select
                options={[
                  { label: 'every', value: 'every' },
                  { label: 'at time', value: 'at time' },
                ]}
                value={discoveryIntervalType}
                onChange={val => {
                  setDiscoveryIntervalType(val);
                  setIsChanged(true);
                }}
                showClearIndicator={false}
              />
            </Box>
            <Box w={'150px'}>
              {discoveryIntervalType.value === 'every' && (
                <Select
                  options={orgSettingsIntervalOptions}
                  value={{
                    label: discoveryInterval / 60 + ' hours',
                    value: discoveryInterval,
                  }}
                  onChange={opt => {
                    setDiscoveryInterval(opt.value);
                    setIsChanged(true);
                  }}
                  showClearIndicator={false}
                />
              )}
              {discoveryIntervalType.value === 'at time' && (
                <TimePicker
                  button={false}
                  value={discoveryTime}
                  onChange={time => {
                    setDiscoveryTime(time);
                    setIsChanged(true);
                  }}
                />
              )}
            </Box>
          </HStack>
          <HStack pt={2}>
            <PrimaryButton
              onClick={handleUpdate}
              isLoading={updateAccountDiscoveryInterval.isLoading}
              isDisabled={!isChanged}
            >
              UPDATE
            </PrimaryButton>
          </HStack>
        </Stack>
      </Stack>
    </OrgConfigBox>
  );
};
